import pick from 'lodash/pick';
import {
  initiatePrivileged,
  shippoGetRates,
  transitionPrivileged,
  shippoGetRatesFromId,
  shippoCreateLabel,
  callToFixStock,
  sendGuestPurchaseConfirmation,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import {
  fetchCurrentUserHasOrdersSuccess,
  fetchCurrentUser,
  sendNotification,
} from '../../ducks/user.duck';
import { signupFromGuestCheckout } from '../../ducks/auth.duck';
import { trackPurchase } from '../../analytics/googleTagManager';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const FETCH_SHIPPO_RATES_REQUEST = 'app/CheckoutPage/FETCH_SHIPPO_RATES_REQUEST';
export const FETCH_SHIPPO_RATES_SUCCESS = 'app/CheckoutPage/FETCH_SHIPPO_RATES_SUCCESS';
export const FETCH_SHIPPO_RATES_ERROR = 'app/CheckoutPage/FETCH_SHIPPO_RATES_ERROR';

export const FETCH_SHIPPO_RATES_FROM_ID_REQUEST =
  'app/CheckoutPage/FETCH_SHIPPO_RATES_FROM_ID_REQUEST';
export const FETCH_SHIPPO_RATES_FROM_ID_SUCCESS =
  'app/CheckoutPage/FETCH_SHIPPO_RATES_FROM_ID_SUCCESS';
export const FETCH_SHIPPO_RATES_FROM_ID_ERROR = 'app/CheckoutPage/FETCH_SHIPPO_RATES_FROM_ID_ERROR';

export const GUEST_SIGNUP_REQUEST = 'app/CheckoutPage/GUEST_SIGNUP_REQUEST';
export const GUEST_SIGNUP_SUCCESS = 'app/CheckoutPage/GUEST_SIGNUP_SUCCESS';
export const GUEST_SIGNUP_ERROR = 'app/CheckoutPage/GUEST_SIGNUP_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  rates: null,
  guestSignupInProgress: false,
  guestSignupError: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    case FETCH_SHIPPO_RATES_REQUEST:
      return { ...state, rates: null };
    case FETCH_SHIPPO_RATES_SUCCESS:
      return { ...state, rates: payload };
    case FETCH_SHIPPO_RATES_ERROR:
      return { ...state, rates: null };

    case FETCH_SHIPPO_RATES_FROM_ID_REQUEST:
      return { ...state, rates: null };
    case FETCH_SHIPPO_RATES_FROM_ID_SUCCESS:
      return { ...state, rates: payload };
    case FETCH_SHIPPO_RATES_FROM_ID_ERROR:
      return { ...state, rates: null };

    case GUEST_SIGNUP_REQUEST:
      return { ...state, guestSignupInProgress: true, guestSignupError: null };
    case GUEST_SIGNUP_SUCCESS:
      return { ...state, guestSignupInProgress: false };
    case GUEST_SIGNUP_ERROR:
      return { ...state, guestSignupInProgress: false, guestSignupError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

export const fetchShippoRatesRequest = () => ({ type: FETCH_SHIPPO_RATES_REQUEST });
export const fetchShippoRatesSuccess = rates => ({
  type: FETCH_SHIPPO_RATES_SUCCESS,
  payload: rates,
});
export const fetchShippoRatesError = e => ({
  type: FETCH_SHIPPO_RATES_ERROR,
  error: true,
  payload: e,
});

export const fetchShippoRatesFromIDRequest = () => ({ type: FETCH_SHIPPO_RATES_FROM_ID_REQUEST });
export const fetchShippoRatesFromIDSuccess = rates => ({
  type: FETCH_SHIPPO_RATES_FROM_ID_SUCCESS,
  payload: rates,
});
export const fetchShippoRatesFromIDError = e => ({
  type: FETCH_SHIPPO_RATES_FROM_ID_ERROR,
  error: true,
  payload: e,
});

export const guestSignupRequest = () => ({ type: GUEST_SIGNUP_REQUEST });
export const guestSignupSuccess = () => ({ type: GUEST_SIGNUP_SUCCESS });
export const guestSignupError = e => ({
  type: GUEST_SIGNUP_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  const resolvedProcessName = processAlias?.split('/')[0] || null;
  const paymentRequestTransitions = [
    'transition/request-payment',
    'transition/request-payment-after-inquiry',
  ];
  const defaultRequestTransition =
    resolvedProcessName === 'default-purchase' || resolvedProcessName === 'default-booking'
      ? 'transition/request-payment'
      : transitionName;
  const effectiveTransitionName = transitionName || defaultRequestTransition;
  const effectiveIsPrivilegedTransition = paymentRequestTransitions.includes(
    effectiveTransitionName
  )
    ? resolvedProcessName === 'default-purchase' || resolvedProcessName === 'default-booking'
    : isPrivilegedTransition;

  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { deliveryMethod } : {};

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: effectiveTransitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: effectiveTransitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && effectiveIsPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (effectiveIsPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const fixStock = body => (dispatch, getState, sdk) => {
  return callToFixStock(body);
};

export const confirmPayment = (
  transactionId,
  transitionName,
  transitionParams = {},
  rateId,
  associatedParams = {},
  clientData = {}
) => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());

  let isAssociatedParam = associatedParams?.isAssociated
    ? { protectedData: { ...associatedParams, ...clientData } }
    : {};

  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: { ...transitionParams, ...isAssociatedParam },
  };
  const queryParams = {
    include: ['booking', 'provider', 'listing'],
    expand: true,
  };

  // Get listing data from state to extract item number and analytics metadata.
  const state = getState();
  const listing = state.CheckoutPage?.listing;
  const itemNumber = listing?.attributes?.publicData?.itemNumber || '';
  const productName = listing?.attributes?.title || '';

  const logEvent = tx => {
    let listingId = tx?.relationships?.listing?.data?.id?.uuid;

    if (listingId) {
      plausible('confirmAndPay', { props: { listingId } });
    }

    trackPurchase({ listing, transaction: tx, orderData: state.CheckoutPage?.orderData });
  };

  // The custom guest receipt is intentionally requested only after Sharetribe accepts
  // transition/confirm-payment. Delivery failures are recorded server-side and must not
  // roll back or hide an already successful payment.
  const requestGuestReceipt = order => {
    const id = order?.id?.uuid;
    if (!id) return;

    sendGuestPurchaseConfirmation({ transactionId: id }).catch(error => {
      log.error(error, 'guest-purchase-confirmation-failed', { transactionId: id });
    });
  };

  if (rateId) {
    return shippoCreateLabel({
      rateId,
      itemNumber,
      productName,
    })
      .then(response => {
        let isAssociatedParamShippo = isAssociatedParam?.protectedData || {};
        const protectedData = {
          shippoLabel: response,
          ...isAssociatedParamShippo,
          ...clientData,
        };

        const bodyParams = {
          id: transactionId,
          transition: transitionName,
          params: { ...transitionParams, protectedData },
        };

        return sdk.transactions
          .transition(bodyParams, queryParams)
          .then(response => {
            const order = response.data.data;
            logEvent(order);
            if (transactionId) {
              dispatch(sendNotification(transactionId));
            }
            dispatch(confirmPaymentSuccess(order.id));
            dispatch(fixStock({ orderId: order.id }));
            requestGuestReceipt(order);
            return order;
          })
          .catch(e => {
            dispatch(confirmPaymentError(storableError(e)));
            const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
            log.error(e, 'initiate-order-failed', {
              ...transactionIdMaybe,
            });
            throw e;
          });
      })
      .catch(e => {
        dispatch(confirmPaymentError(storableError(e)));
        throw e;
      });
  }

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const order = response.data.data;
      logEvent(order);
      if (transactionId) {
        dispatch(sendNotification(transactionId));
      }
      dispatch(confirmPaymentSuccess(order.id));
      dispatch(fixStock({ orderId: order.id }));
      requestGuestReceipt(order);
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { deliveryMethod } : {};

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  // Guard: Don't refetch if already fetched successfully
  const { stripeCustomerFetched } = getState().CheckoutPage;
  if (stripeCustomerFetched) {
    return Promise.resolve();
  }

  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

export const fetchShippoRates = params => (dispatch, getState, sdk) => {
  const { addressFrom, addressTo, parcels } = params;

  dispatch(fetchShippoRatesRequest());

  return shippoGetRates({ addressFrom, addressTo, parcels })
    .then(response => {
      dispatch(fetchShippoRatesSuccess(response));
    })
    .catch(e => {
      dispatch(fetchShippoRatesError(storableError(e)));
    });
};

export const fetchSippoRatesFromID = id => (dispatch, getState, sdk) => {
  dispatch(fetchShippoRatesFromIDRequest());
  return shippoGetRatesFromId(id)
    .then(response => {
      dispatch(fetchShippoRatesFromIDSuccess(response.data));
      return response;
    })
    .catch(e => {
      dispatch(fetchShippoRatesFromIDError(storableError(e)));
      throw e;
    });
};

// Guest signup thunk - creates new account, logs out guest, logs in with new account
export const guestSignup = signupData => (dispatch, getState, sdk) => {
  dispatch(guestSignupRequest());

  // Store current URL to redirect back after signup
  const currentUrl = window.location.href;
  sessionStorage.setItem('guest_signup_return_url', currentUrl);

  // Prepare signup data with buyer userType
  const signupParams = {
    email: signupData.email,
    password: signupData.password,
    firstName: signupData.fname,
    lastName: signupData.lname,
    publicData: {
      userType: 'buyer',
    },
    protectedData: {
      phoneNumber: signupData.phone,
    },
  };

  return dispatch(signupFromGuestCheckout(signupParams))
    .then(() => {
      dispatch(guestSignupSuccess());
      // Set flags for the redirect process
      sessionStorage.setItem('guest_signup_completed', 'true');
      sessionStorage.setItem(
        'guest_signup_new_credentials',
        JSON.stringify({
          email: signupData.email,
          password: signupData.password,
        })
      );
      // Instead of immediate redirect, let the component handle it
      // This ensures proper cleanup and data restoration
      return Promise.resolve();
    })
    .catch(e => {
      dispatch(guestSignupError(e));
      // Clear any partial data on error
      sessionStorage.removeItem('guest_signup_completed');
      sessionStorage.removeItem('guest_signup_new_credentials');
      sessionStorage.removeItem('guest_signup_return_url');
      window.localStorage.removeItem('CheckoutPage_backup');
      throw e;
    });
};
